import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Typography,
  Tabs,
  Collapse,
  Modal,
  App,
} from "antd";
import "./courseForm.scss";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { resolveEntityId } from "../../../utils/route-entity-id";
import {
  createCourse,
  getCourseById,
  updateCourse,
  confirmTitleCascade,
  CascadeEntity,
  CascadeSkipReason,
  ConfirmedRename,
  TitleCascadePreview,
} from "../../../services/courses/courses.service";
import {
  createProject as createProjectService,
  updateProject as updateProjectService,
  getProjectById,
  syncProjectCourses,
  updatePhaseTitle,
  replaceProjectLessons,
} from "../../../services/projects/projects.service";
import {
  ProjectInput,
  ProjectPhase,
} from "../../../services/projects/projects.types";
import {
  CourseEntityInput,
  CourseEvaluation,
  NemPrimaryLinkingItem,
  RelevantCourseItem,
} from "../../../services/courses/courses.types";
import { useLessons } from "../../../hooks/useLessons";
import { useCourses } from "../../../hooks/useCourse";
import { usePrograms } from "../../../hooks/usePrograms";
import { useResources } from "../../../hooks/useResource";
import { getProgramsByCourse } from "../../../services/programs/programs.service";
import { ProgramEntity } from "../../../services/programs/programs.types";
import type { UploadRequestOption } from "rc-upload/lib/interface";
import ActivityIndicator from "../../ActivityIndicator";
import { useSubjects } from "../../../hooks/useSubject";
import {
  handleSubmitFormResponseManage,
  customUpload,
  getApiErrorMessage,
} from "../../../utils";
import { useDispatch } from "react-redux";
import {
  emptyTrimesterEvaluation,
  evaluationQuestionTitle,
  initialValuesCourse,
  isValidQuestionPoints,
  TrimesterEvaluationState,
} from "./utils";
import { DIAGNOSTIC_QUESTION_AXES_BY_SUBJECT_CODE } from "./constants";
import { useNotification } from "../../../hooks/useNotification";
import { LessonMinimalResponse } from "../../../types/api/getLesson";
import { CourseMinimalResponse } from "../../../types/api/getCourse";
import NormalCourseForm from "./components/NormalCourseForm";
import DiagnosticCourseForm, {
  DiagnosticEvaluationMode,
} from "./components/DiagnosticCourseForm";
import { createEmptyQuestion } from "./components/CourseQuestionsForm";
import ProjectCourseForm from "./components/ProjectCourseForm";
import EvaluationCourseForm from "./components/EvaluationCourseForm";
import CourseResourceSelector, {
  CourseResourceRow,
  nextResourceRowUid,
} from "./components/CourseResourceSelector";
import CascadeConfirmModal, {
  CASCADE_ENTITY_LABEL,
} from "./components/CascadeConfirmModal";
import EntityHistory from "../../EntityHistory";
import LastModified from "../../LastModified";

const { Title } = Typography;
const { Option } = Select;

// --- Course-title rename cascade (SCRUM-797 BO-1/BO-2) ---
// Copy mirrors emmi-frontend's title-cascade-modal.tsx verbatim so both
// clients say the same thing to admins.

const cascadeSkipReasonLabel = (reason: CascadeSkipReason): string =>
  reason === "title_changed"
    ? "Su título cambió antes de confirmar, así que no se aplicó este cambio."
    : "Ahora se usa en más de un curso, así que no se aplicó este cambio.";

// Plain-string summary (the `notify` helper's `description` is a string, not
// a ReactNode) surfacing what was applied vs. skipped after a confirm.
const cascadeResultDescription = (
  applied: { entity: CascadeEntity; id: number; new_title: string }[],
  skipped: { entity: CascadeEntity; id: number; reason: CascadeSkipReason }[],
): string => {
  const parts: string[] = [];
  if (applied.length > 0) {
    parts.push(
      `Se renombraron: ${applied
        .map((row) => `${CASCADE_ENTITY_LABEL[row.entity]}: ${row.new_title}`)
        .join(", ")}`,
    );
  }
  if (skipped.length > 0) {
    parts.push(
      `No se aplicaron: ${skipped
        .map(
          (row) =>
            `${CASCADE_ENTITY_LABEL[row.entity]} — ${cascadeSkipReasonLabel(row.reason)}`,
        )
        .join(", ")}`,
    );
  }
  return parts.join(" ");
};

interface LessonSelected {
  id: number;
  title: string;
  phase_id?: number;
  // Stable per-row id for drag-and-drop reordering, stripped before submit. It
  // survives the object-spread in handleLessonChange so selecting a lesson does
  // not remount its sortable row.
  _uid?: string;
}

type CourseType = "normal" | "diagnostic" | "evaluation" | "project";

let lessonRowSeq = 0;
const nextLessonRowUid = () => `lesson-row-${(lessonRowSeq += 1)}`;

const CourseForm: React.FC = () => {
  const { notification } = App.useApp();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  // Manejar el state que puede ser un número o un objeto { id, isProject, defaultTab }
  const locationState = location?.state;
  const { id: idParam } = useParams();
  // URL param first so deep links (content map, refresh, shared URLs) work;
  // navigation state remains the fallback used by the courses list, and
  // isProject/defaultTab still travel exclusively through state.
  const courseId = resolveEntityId(
    idParam,
    typeof locationState === "number" ? locationState : locationState?.id
  );
  // The project flag also travels in the URL (?project=1) so deep links and
  // refreshed/shared project URLs fetch from the right endpoint; navigation
  // state stays as the legacy fallback.
  const isEditingProject =
    searchParams.get("project") === "1" ||
    (typeof locationState === "object"
      ? locationState?.isProject ?? false
      : false);
  // On creation, the project mode is also fully derivable from the URL
  // (?project=1) so it survives a fresh load in a new tab (middle/Cmd+click),
  // where router state is absent — mirroring how isEditingProject already
  // reads the URL. Navigation state stays as the same-tab fallback used by the
  // courses list's left-click flow.
  const defaultTab =
    !courseId && searchParams.get("project") === "1"
      ? "project"
      : typeof locationState === "object"
        ? locationState?.defaultTab
        : undefined;

  const { getAllLessonsData } = useLessons();
  const { courses, loading: loadingCourses, getAllCoursesData } = useCourses();
  const { programs, loading: loadingPrograms } = usePrograms();
  const { subjects, loading: loadingSubjects } = useSubjects();
  const { resources } = useResources();
  // Current subject, watched reactively from the form store (SCRUM-740): it
  // covers both edit (setFieldsValue) and creation (Form.Item-controlled
  // Select). The axis catalog for diagnostic questions is keyed by its code.
  const watchedSubjectId = Form.useWatch("subject_id", form);
  const currentSubjectCode = subjects.find(
    (subject: { id: number; code: string }) => subject.id === watchedSubjectId,
  )?.code;
  const availableQuestionAxes: string[] = currentSubjectCode
    ? DIAGNOSTIC_QUESTION_AXES_BY_SUBJECT_CODE[currentSubjectCode] ?? []
    : [];
  const [availableLessons, setAvailableLessons] = useState<
    LessonMinimalResponse[]
  >([]);
  const [loadingLessons, setLoadingLessons] = useState<boolean>(false);
  const [selectedLessons, setSelectedLessons] = useState<LessonSelected[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<
    {
      id: number;
      title: string;
      program_id?: number | null;
      week?: number | null;
    }[]
  >([]);
  const [allCourses, setAllCourses] = useState<CourseMinimalResponse[]>([]);
  const [programsbyCourse, setProgramsbyCourse] = useState<{
    [courseId: number]: ProgramEntity[];
  }>({});
  const [relevantCourses, setRelevantCourses] = useState<RelevantCourseItem[]>(
    [],
  );
  const [nemPrimaryLinkings, setNemPrimaryLinkings] = useState<
    NemPrimaryLinkingItem[]
  >([]);
  const [tagsSelected, setTagsSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [claveError, setClaveError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Synchronous re-entrancy guard: `loading` lags a render behind, so a fast
  // double click on Guardar would fire two create/update calls.
  const submitInFlightRef = useRef(false);
  const [initialValues, setInitialValues] =
    useState<CourseEntityInput>(initialValuesCourse);
  const [uploadingProjectImage, setUploadingProjectImage] = useState(false);
  const [projectImageUrl, setProjectImageUrl] = useState<string | null>(null);
  const [courseEvaluations, setCourseEvaluations] = useState<
    CourseEvaluation[]
  >([]);
  // Modo de evaluación del curso diagnóstico: rúbricas (Kinder) o preguntas
  // con puntaje + tooltip (Primaria). Se infiere de los datos al cargar.
  const [evaluationMode, setEvaluationMode] =
    useState<DiagnosticEvaluationMode>("rubricas");
  const [projectRubrics, setProjectRubrics] = useState<
    Array<{
      rubric_achieved: string;
      rubric_in_process: string;
      rubric_needs_support: string;
      key_learnings: string;
    }>
  >([]);
  const [projectPhases, setProjectPhases] = useState<ProjectPhase[]>([
    {
      id: -1, // ID negativo temporal basado en order_by
      phase_type: "STARTING_POINT",
      title: "Punto de partida y planeación",
      order_by: 1,
      lessons: [],
    },
    {
      id: -2,
      phase_type: "EXPLORATION",
      title: "¡A trabajar! - Exploración y organización",
      order_by: 2,
      lessons: [],
    },
    {
      id: -3,
      phase_type: "PRODUCTION",
      title: "¡A trabajar! - Producción y creación",
      order_by: 3,
      lessons: [],
    },
    {
      id: -4,
      phase_type: "COMMUNICATION",
      title: "Comunicamos nuestros logros y reflexiones",
      order_by: 4,
      lessons: [],
    },
  ]);
  const [initialDiagnosticValue, setInitialDiagnosticValue] =
    useState<boolean>(false);
  const [courseType, setCourseType] = useState<CourseType>("normal");
  const [initialCourseType, setInitialCourseType] =
    useState<CourseType>("normal");
  // Evaluación trimestral: program, trimester and the question per course. It
  // is kept apart from courseEvaluations so the Diagnóstico tab never sees it.
  const [trimesterEvaluation, setTrimesterEvaluation] =
    useState<TrimesterEvaluationState>(emptyTrimesterEvaluation);
  const [courseResourceRows, setCourseResourceRows] = useState<
    CourseResourceRow[]
  >([]);
  const { notify } = useNotification();
  // Pending cascade decision (SCRUM-797 BO-2): a controlled <Modal>, not
  // Modal.confirm — per-row checkboxes need reactive state and a reactively
  // disabled OK button, which a one-shot confirm config cannot host.
  const [cascadeContext, setCascadeContext] = useState<{
    courseId: number;
    preview: TitleCascadePreview;
    resolve: () => void;
  } | null>(null);
  const [cascadeConfirming, setCascadeConfirming] = useState(false);

  // Función para obtener los campos de evaluación
  const getEvaluationFields = () => [
    initialValues.key_learnings,
    initialValues.rubric_achieved,
    initialValues.rubric_in_process,
    initialValues.rubric_needs_support,
  ];

  // Función para validar campos de evaluación
  const validateEvaluationFields = () => {
    const evaluationFields = getEvaluationFields();
    const hasAnyEvaluationField = evaluationFields.some(
      (field) => field && field.trim() !== "",
    );
    const hasAllEvaluationFields = evaluationFields.every(
      (field) => field && field.trim() !== "",
    );

    return {
      hasAny: hasAnyEvaluationField,
      hasAll: hasAllEvaluationFields,
      isValid: !hasAnyEvaluationField || hasAllEvaluationFields,
    };
  };

  // Función para determinar si los campos de evaluación son requeridos
  const getEvaluationFieldsValidation = () => {
    const { hasAny } = validateEvaluationFields();

    if (hasAny) {
      return [
        {
          required: true,
          message:
            "Este campo es obligatorio cuando se completan criterios de evaluación",
        },
      ];
    }
    return [];
  };

  const fetchInfo = async () => {
    setLoading(true);
    try {
      // Cargar todas las lecciones disponibles
      setLoadingLessons(true);
      const lessonsData = await getAllLessonsData();
      setAvailableLessons(lessonsData);
      setLoadingLessons(false);

      if (courseId) {
        // Si estamos editando un proyecto, usar el endpoint de proyectos
        if (isEditingProject) {
          const response = await getProjectById(courseId);
          if (response) {
            // Configurar como proyecto
            setCourseType("project");
            setInitialCourseType("project");

            // Establecer valores iniciales del proyecto
            const projectValues = {
              title: response.title,
              clave: response.clave ?? "",
              content: "",
              pda: "",
              learning_goals: "",
              subject_id: 7, // Proyectos siempre tienen subject_id = 7
              lessons: [],
              tags: [],
              book_linking_nem: "",
              ability: "",
              stroke_goals: "",
              syllabic_phonetic_goals: "",
              communication_goals: "",
              structures: "",
              student_structures: "",
              vocabulary_new_words: "",
              vocabulary_review_words: "",
              phonemics_reading_goals: "",
              key_learnings: "",
              rubric_achieved: "",
              rubric_in_process: "",
              rubric_needs_support: "",
              axis: "",
              diagnostic: false,
              // Campos específicos del proyecto
              trigger_question: response.trigger_question || "",
              final_product: response.final_product || "",
              summary: response.summary || "",
              human_to_community: response.human_to_community || "",
              ejes_articuladores: response.ejes_articuladores || [],
              habilidades_siglo_xxi: response.habilidades_siglo_xxi || [],
              weeks: response.weeks || "",
              trimester: response.trimester || 1,
              file_id: response.file_id || null,
            };

            setInitialValues(projectValues);

            // Cargar URL de la imagen si existe
            if (response.file_url) {
              setProjectImageUrl(response.file_url);
            }

            // Actualizar el formulario de Ant Design con todos los valores
            form.setFieldsValue(projectValues);

            // Cargar los cursos seleccionados del proyecto
            if (response.courses && response.courses.length > 0) {
              // Pre-cargar los programas de todos los cursos del proyecto
              const uniqueCourseIds = Array.from(
                new Set<number>(
                  response.courses.map((c) => c.id).filter((id) => id > 0),
                ),
              );
              const programsMap: { [courseId: number]: ProgramEntity[] } = {};
              await Promise.all(
                uniqueCourseIds.map(async (cId: number) => {
                  const progs = await getProgramsByCourse(cId);
                  programsMap[cId] = progs;
                }),
              );
              setProgramsbyCourse(programsMap);

              // Enriquecer cursos con el week del programa asociado
              const coursesWithWeek = response.courses.map((c) => {
                if (!c.program_id) return c;
                const prog = (programsMap[c.id] ?? []).find(
                  (p) => p.id === c.program_id,
                );
                return { ...c, week: prog?.week ?? null };
              });
              setSelectedCourses(coursesWithWeek);
            }

            // Cargar las fases del proyecto
            if (response.phases && response.phases.length > 0) {
              setProjectPhases(response.phases);

              // Extraer las lecciones con phase_id de todas las fases
              const lessonsWithPhase = response.phases.flatMap((phase) =>
                phase.lessons.map((lesson) => ({
                  ...lesson,
                  phase_id: phase.id,
                  _uid: nextLessonRowUid(),
                })),
              );
              setSelectedLessons(lessonsWithPhase);
            } else {
              // Si no hay fases, cargar lecciones sin fase (compatibilidad)
              if (response.lessons && response.lessons.length > 0) {
                setSelectedLessons(
                  response.lessons.map((lesson) => ({
                    ...lesson,
                    _uid: nextLessonRowUid(),
                  })),
                );
              }
            }

            // Cargar las rúbricas del proyecto si existen
            if (response.evaluations && response.evaluations.length > 0) {
              const rubrics = response.evaluations.map(
                (evaluation: CourseEvaluation) => ({
                  rubric_achieved: evaluation.rubric_achieved || "",
                  rubric_in_process: evaluation.rubric_in_process || "",
                  rubric_needs_support: evaluation.rubric_needs_support || "",
                  key_learnings: evaluation.key_learnings || "",
                }),
              );
              setProjectRubrics(rubrics);

              // Actualizar los valores del formulario de Ant Design
              form.setFieldsValue({
                projectRubrics: rubrics,
              });
            } else {
              // Inicializar con una rúbrica vacía
              const emptyRubric = [
                {
                  rubric_achieved: "",
                  rubric_in_process: "",
                  rubric_needs_support: "",
                  key_learnings: "",
                },
              ];
              setProjectRubrics(emptyRubric);

              form.setFieldsValue({
                projectRubrics: emptyRubric,
              });
            }
          } else {
            setLoadFailed(true);
            notification.error({
              message: "Error",
              description: "El proyecto no pudo ser encontrado.",
              placement: "top",
              duration: 3,
            });
          }
        } else {
          // Cargar como curso normal
          const response = await getCourseById(courseId);
          if (response) {
            const courseValues = {
              title: response.title,
              clave: response.clave ?? "",
              content: response.content,
              pda: response.pda,
              learning_goals: response.learning_goals,
              subject_id: response.subject_id,
              lessons: response.lessons,
              tags: response.tags,
              book_linking_nem: response.book_linking_nem,
              ability: response.ability,
              stroke_goals: response.stroke_goals,
              syllabic_phonetic_goals: response.syllabic_phonetic_goals,
              communication_goals: response.communication_goals,
              structures: response.structures,
              student_structures: response.student_structures,
              vocabulary_new_words: response.vocabulary_new_words,
              vocabulary_review_words: response.vocabulary_review_words,
              phonemics_reading_goals: response.phonemics_reading_goals,
              key_learnings: response.key_learnings,
              rubric_achieved: response.rubric_achieved,
              rubric_in_process: response.rubric_in_process,
              rubric_needs_support: response.rubric_needs_support,
              axis: response.axis,
              diagnostic: response.diagnostic || false,
              ejes_articuladores: response.ejes_articuladores || [],
            };

            setInitialValues(courseValues);
            setRelevantCourses(response.relevant_courses || []);
            setNemPrimaryLinkings(response.nem_primary_linkings || []);

            // Actualizar el formulario de Ant Design con todos los valores
            form.setFieldsValue(courseValues);

            // Guardamos el valor inicial de diagnostic para el bloqueo
            const isProject = response.subject_id === 7; // Proyectos tienen subject_id = 7
            const isDiagnostic = response.diagnostic || false;
            const isEvaluation = response.trimester_evaluation || false;

            if (isProject) {
              setCourseType("project");
              setInitialCourseType("project");
            } else if (isDiagnostic) {
              setCourseType("diagnostic");
              setInitialCourseType("diagnostic");
            } else if (isEvaluation) {
              setCourseType("evaluation");
              setInitialCourseType("evaluation");
              setTrimesterEvaluation({
                programId: response.evaluation_program_id ?? undefined,
                trimester: response.evaluation_trimester ?? undefined,
                questions: response.courseEvaluations ?? [],
              });
            } else {
              setCourseType("normal");
              setInitialCourseType("normal");
            }

            setInitialDiagnosticValue(isDiagnostic);

            if (response.lessons.length > 0) {
              setSelectedLessons(
                response.lessons.map((lesson) => ({
                  ...lesson,
                  _uid: nextLessonRowUid(),
                })),
              );
            }

            if (response.tags.length > 0) {
              setTagsSelected(response.tags.map((tag) => tag.name));
            }

            // Cargar course_resources si el curso los tiene
            if (
              response.course_resources &&
              response.course_resources.length > 0
            ) {
              const rows: CourseResourceRow[] = response.course_resources
                .filter((cr) => cr.resource !== null)
                .map((cr) => ({
                  resource_id: cr.resource_id,
                  order_by: cr.order_by,
                  resourceType: cr.resource!.type,
                  _uid: nextResourceRowUid(),
                }));
              setCourseResourceRows(rows);
            } else {
              setCourseResourceRows([]);
            }

            // Si es un curso diagnóstico y tiene evaluaciones, las cargamos
            if (response.diagnostic && response.courseEvaluations) {
              setCourseEvaluations(response.courseEvaluations);

              // Inferir el modo: si alguna evaluación tiene puntaje es un
              // diagnóstico por preguntas (Primaria)
              setEvaluationMode(
                response.courseEvaluations.some(
                  (evaluation) => evaluation.max_points != null,
                )
                  ? "preguntas"
                  : "rubricas",
              );

              // Actualizar el formulario con las evaluaciones
              form.setFieldsValue({
                courseEvaluations: response.courseEvaluations,
              });
            } else {
              // Si no es diagnóstico, inicializamos con una evaluación vacía
              const emptyEvaluation = [
                {
                  key_learnings: "",
                  rubric_achieved: "",
                  rubric_in_process: "",
                  rubric_needs_support: "",
                  axis: "",
                },
              ];
              setCourseEvaluations(emptyEvaluation);

              form.setFieldsValue({
                courseEvaluations: emptyEvaluation,
              });
            }
          } else {
            setLoadFailed(true);
            notification.error({
              message: "Error",
              description: "El curso no pudo ser encontrado.",
              placement: "top",
              duration: 3,
            });
          }
        }
      } else {
        // Para cursos nuevos, inicializar con una evaluación vacía
        setCourseEvaluations([
          {
            key_learnings: "",
            rubric_achieved: "",
            rubric_in_process: "",
            rubric_needs_support: "",
            axis: "",
          },
        ]);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setLoadingLessons(false);
      setLoadFailed(true);
      console.error(error);
      const backendMessage = error?.response?.data?.message;
      notification.error({
        message: "Error",
        description: backendMessage
          ? `No se pudo cargar la información. Reintentá o volvé al listado.\n${backendMessage}`
          : "No se pudo cargar la información. Reintentá o volvé al listado.",
        placement: "top",
        duration: 3,
      });
    }
  };

  useEffect(() => {
    fetchInfo();
  }, [courseId, isEditingProject]);

  // Establecer la pestaña inicial basada en el defaultTab del state
  useEffect(() => {
    if (!courseId && defaultTab) {
      setCourseType(defaultTab as CourseType);
      if (defaultTab === "project") {
        setInitialValues((prev) => ({
          ...prev,
          subject_id: 7, // Proyectos siempre tienen subject_id = 7
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab, courseId]);

  // Cargar todos los cursos para la sección "Cursos relevantes" (solo aplica a cursos,
  // no a proyectos). Si no cargamos esta lista, el Select queda vacío.
  useEffect(() => {
    const loadAllCourses = async () => {
      const isCourseForm =
        courseType === "normal" ||
        courseType === "diagnostic" ||
        (courseId &&
          (initialCourseType === "normal" ||
            initialCourseType === "diagnostic"));

      if (!isCourseForm) return;

      try {
        const coursesData = await getAllCoursesData();
        setAllCourses(coursesData);
      } catch (error) {
        console.error("Error loading all courses:", error);
      }
    };

    loadAllCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseType, initialCourseType, courseId]);

  const handleProjectImageUpload = async (options: UploadRequestOption) => {
    setUploadingProjectImage(true);
    try {
      const result = await customUpload(options);
      if (result.fileName && result.url) {
        setInitialValues((prevState) => ({
          ...prevState,
          file_id: result.fileName,
        }));
        setProjectImageUrl(result.url);
      }
    } catch (error) {
      console.error("Error uploading project image:", error);
      notify("error", "Error", "No se pudo subir la imagen");
    } finally {
      setUploadingProjectImage(false);
    }
  };

  /**
   * Wraps `updateCourse` so a title change carrying pending cascade renames
   * is confirmed (or explicitly skipped) BEFORE this promise resolves.
   * `handleSubmitFormResponseManage` (utils/index.ts) calls `navigate(-1)`
   * immediately once its update call resolves successfully, so a dialog
   * shown after that point would never be seen — it has to live inside this
   * wrapper. Also strips `titleCascadePreview` before returning, since
   * `store/slices/course.ts`'s `updateCourse` reducer does a wholesale
   * `state.courses[index] = action.payload.data` and that preview object
   * does not belong in Redux state.
   */
  const updateCourseWithCascade = async (
    id: number,
    data: CourseEntityInput,
  ) => {
    const response = await updateCourse(id, data);
    if (!response) return response;

    const { titleCascadePreview, ...course } = response as CourseEntityInput & {
      id: number;
      titleCascadePreview?: TitleCascadePreview | null;
    };

    if (!titleCascadePreview || titleCascadePreview.renames.length === 0) {
      return course;
    }

    await new Promise<void>((resolve) => {
      setCascadeContext({ courseId: id, preview: titleCascadePreview, resolve });
    });

    return course;
  };

  const handleCascadeCancel = () => {
    cascadeContext?.resolve();
    setCascadeContext(null);
  };

  const handleCascadeConfirm = async (renames: ConfirmedRename[]) => {
    if (!cascadeContext) return;
    setCascadeConfirming(true);
    try {
      const result = await confirmTitleCascade(cascadeContext.courseId, renames);
      if (result) {
        notify(
          "info",
          result.skipped.length > 0
            ? "Algunos cambios relacionados no se aplicaron"
            : "Cambios relacionados aplicados",
          cascadeResultDescription(result.applied, result.skipped),
        );
      }
    } finally {
      setCascadeConfirming(false);
      cascadeContext.resolve();
      setCascadeContext(null);
    }
  };

  // The antd static notification does not render in this app, so a duplicate
  // clave is also shown inline under its field. It lives in state because the
  // form remounts when loading ends, which wipes errors set on the form.
  const showDuplicateClave = (error: any) => {
    const message = error?.response?.data?.message;
    if (error?.response?.status === 409 && message) {
      setClaveError(message);
    }
  };

  const handleSubmit = async () => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    try {
      await submitCourse();
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const submitCourse = async () => {
    const isProjectType =
      courseType === "project" || (courseId && initialCourseType === "project");

    // Validación de lecciones (no aplica a proyectos)
    if (
      !isProjectType &&
      selectedLessons.some((lesson) => lesson.id === 0 || !lesson.id)
    ) {
      notify(
        "error",
        "Error en Lecciones",
        "Todas las filas deben tener una lección seleccionada.",
      );
      return;
    }

    // Validación específica según el tipo de curso
    const isDiagnosticType =
      courseType === "diagnostic" ||
      (courseId && initialCourseType === "diagnostic");
    const isEvaluationType =
      courseType === "evaluation" ||
      (courseId && initialCourseType === "evaluation");

    if (isEvaluationType) {
      const { programId, trimester, questions } = trimesterEvaluation;
      if (!programId || !trimester || questions.length === 0) {
        notify(
          "error",
          "Preguntas incompletas",
          "Selecciona un programa y un trimestre con cursos para precargar las preguntas de la evaluación.",
        );
        return;
      }
      if (
        !questions.every((question) =>
          isValidQuestionPoints(question.max_points),
        )
      ) {
        notify(
          "error",
          "Preguntas incompletas",
          "Cada pregunta necesita un puntaje entero de 1 o más.",
        );
        return;
      }
    } else if (isDiagnosticType && evaluationMode === "preguntas") {
      // Para diagnósticos por preguntas (Primaria), cada pregunta necesita puntaje
      const validQuestions = courseEvaluations.filter(
        (question) => (question.max_points ?? 0) > 0,
      );
      if (validQuestions.length === 0) {
        notify(
          "error",
          "Preguntas incompletas",
          "Agrega al menos una pregunta con puntaje para el diagnóstico.",
        );
        return;
      }
    } else if (isDiagnosticType || isProjectType) {
      // Para cursos diagnósticos y proyectos, validar que las evaluaciones con contenido estén completas (excluyendo el eje que es opcional)
      const incompleteEvaluation = courseEvaluations.find((evaluation) => {
        const hasAnyField =
          evaluation.key_learnings ||
          evaluation.rubric_achieved ||
          evaluation.rubric_in_process ||
          evaluation.rubric_needs_support;
        const hasAllRequiredFields =
          evaluation.key_learnings &&
          evaluation.rubric_achieved &&
          evaluation.rubric_in_process &&
          evaluation.rubric_needs_support;
        return hasAnyField && !hasAllRequiredFields;
      });

      if (incompleteEvaluation) {
        notify(
          "error",
          "Evaluación Incompleta",
          "Si completas algún campo de una evaluación, debes completar todos los campos obligatorios (Aprendizajes clave y las tres Rúbricas). El campo Eje es opcional.",
        );
        return;
      }
    } else {
      // Para cursos regulares, usar la validación anterior
      const evaluationValidation = validateEvaluationFields();
      if (!evaluationValidation.isValid) {
        notify(
          "error",
          "Campos de Evaluación Incompletos",
          "Si completas algún campo de Aprendizajes Clave o Rúbricas, debes completar todos los campos de evaluación.",
        );
        return;
      }
    }

    setLoading(true);
    setClaveError(null);

    try {
      // Si es un proyecto, usar el servicio de proyectos
      const isProjectType =
        courseType === "project" ||
        (courseId && initialCourseType === "project");

      if (isProjectType) {
        // Filtrar rúbricas que tengan al menos un campo completo
        const validRubrics = projectRubrics.filter(
          (rubric) =>
            rubric.key_learnings ||
            rubric.rubric_achieved ||
            rubric.rubric_in_process ||
            rubric.rubric_needs_support,
        );

        const evaluations: CourseEvaluation[] = validRubrics.map((rubric) => ({
          key_learnings: rubric.key_learnings || "",
          rubric_achieved: rubric.rubric_achieved || "",
          rubric_in_process: rubric.rubric_in_process || "",
          rubric_needs_support: rubric.rubric_needs_support || "",
          axis: "", // No se usa en proyectos
        }));

        const projectInput: ProjectInput = {
          title: initialValues.title,
          clave: initialValues.clave?.trim() || null,
          trigger_question: initialValues.trigger_question || undefined,
          final_product: initialValues.final_product || undefined,
          summary: initialValues.summary || undefined,
          human_to_community: initialValues.human_to_community || undefined,
          ejes_articuladores:
            initialValues.ejes_articuladores &&
            initialValues.ejes_articuladores.length > 0
              ? initialValues.ejes_articuladores
              : undefined,
          habilidades_siglo_xxi:
            initialValues.habilidades_siglo_xxi &&
            initialValues.habilidades_siglo_xxi.length > 0
              ? initialValues.habilidades_siglo_xxi
              : undefined,
          weeks:
            initialValues.weeks && initialValues.weeks.trim() !== ""
              ? initialValues.weeks
              : undefined, // Solo enviar si tiene valor
          trimester: initialValues.trimester || undefined,
          file_id: initialValues.file_id || null,
          program_ids: [], // No se asignan programas al crear, se hará desde la vista de programas
          courses: selectedCourses
            .filter((c) => c.id > 0)
            .map((c) => ({
              course_id: c.id,
              program_id: c.program_id,
            })),
          // Incluir lecciones con sus fases
          lessons: selectedLessons
            .filter((l) => l.id > 0 && l.phase_id)
            .map((lesson) => {
              // Encontrar la fase temporal para obtener el order_by
              const tempPhase = projectPhases.find(
                (p) => p.id === lesson.phase_id,
              );
              return {
                lesson_id: lesson.id,
                phase_order_by: tempPhase?.order_by || 1,
              };
            }),
          evaluations: evaluations,
        };

        if (courseId) {
          // Para actualizar, excluir program_ids, course_ids, courses y lessons que el backend maneja por separado
          const {
            program_ids,
            course_ids,
            courses,
            lessons,
            ...projectUpdateData
          } = projectInput;
          await updateProjectService(courseId, projectUpdateData);

          // Sincronizar los cursos del proyecto (ahora con información de programa)
          const coursesToSync = selectedCourses
            .filter((c) => c.id > 0)
            .map((c) => ({
              course_id: c.id,
              program_id: c.program_id,
            }));
          await syncProjectCourses(courseId, { courses: coursesToSync });

          // Actualizar títulos de fases si cambiaron
          for (const phase of projectPhases) {
            if (phase.id > 0) {
              await updatePhaseTitle(courseId, phase.id, phase.title);
            }
          }

          // Un solo reemplazo transaccional. El backend valida que cada fase
          // pertenezca al proyecto y aplica todo o nada, así que desaparece la
          // ventana en la que el proyecto quedaba sin lecciones si fallaba una
          // de las llamadas intermedias.
          await replaceProjectLessons(
            courseId,
            selectedLessons
              .filter((lesson) => lesson.id > 0 && lesson.phase_id)
              .map((lesson) => ({
                lesson_id: lesson.id,
                phase_id: lesson.phase_id as number,
              })),
          );

          notify(
            "success",
            "Proyecto actualizado",
            "El proyecto se actualizó correctamente",
          );
        } else {
          // Crear proyecto con lecciones incluidas
          const createdProject = await createProjectService(projectInput);

          if (createdProject) {
            // El proyecto ya existe en este punto. Si falla el ajuste de los
            // nombres de las fases se avisa aparte y se navega igual: dejar
            // que el error suba haría que el usuario reintente el formulario y
            // termine creando un proyecto duplicado.
            try {
              const fullProject = await getProjectById(createdProject.id);

              for (let i = 0; i < projectPhases.length; i++) {
                const phaseFromServer = fullProject.phases?.find(
                  (p) => p.order_by === projectPhases[i].order_by,
                );
                if (
                  phaseFromServer &&
                  projectPhases[i].title !== phaseFromServer.title
                ) {
                  await updatePhaseTitle(
                    createdProject.id,
                    phaseFromServer.id,
                    projectPhases[i].title,
                  );
                }
              }
            } catch (phaseError) {
              console.error(
                `El proyecto ${createdProject.id} se creó pero fallaron los nombres de las fases:`,
                phaseError,
              );
              notify(
                "warning",
                "Proyecto creado, con un pendiente",
                "El proyecto se creó, pero no se pudieron guardar los nombres de las fases. Editalo para volver a intentarlo.",
              );
              navigate("/courses");
              return;
            }
          }

          notify(
            "success",
            "Proyecto creado",
            "El proyecto se creó correctamente",
          );
        }

        navigate("/courses");
        return;
      }

      // Lógica original para cursos
      const courseInput: CourseEntityInput = {
        title: initialValues.title,
        clave: initialValues.clave?.trim() || null,
        content: initialValues.content,
        pda: initialValues.pda,
        learning_goals: initialValues.learning_goals,
        book_linking_nem: initialValues.book_linking_nem,
        ability: initialValues.ability,
        stroke_goals: initialValues.stroke_goals,
        syllabic_phonetic_goals: initialValues.syllabic_phonetic_goals,
        communication_goals: initialValues.communication_goals,
        structures: initialValues.structures,
        student_structures: initialValues.student_structures,
        vocabulary_new_words: initialValues.vocabulary_new_words,
        vocabulary_review_words: initialValues.vocabulary_review_words,
        phonemics_reading_goals: initialValues.phonemics_reading_goals,
        subject_id: initialValues.subject_id,
        tags: tagsSelected.map((tag) => ({ name: tag })),
        // Drop the drag-and-drop-only _uid; the array order already encodes
        // order_by for the backend (non-project lessons carry no phase).
        lessons: selectedLessons.map((lesson) => ({
          id: lesson.id,
          title: lesson.title,
        })),
        diagnostic: initialValues.diagnostic,
        ejes_articuladores: initialValues.ejes_articuladores,
        relevant_courses: relevantCourses,
        nem_primary_linkings: nemPrimaryLinkings,
        course_resources: courseResourceRows
          .filter((row) => row.resource_id !== null)
          .map((row) => ({
            resource_id: row.resource_id!,
            order_by: row.order_by,
          })),
      };

      // Agregar campos según el tipo de curso
      if (isEvaluationType) {
        courseInput.diagnostic = false;
        courseInput.trimester_evaluation = true;
        courseInput.evaluation_program_id = trimesterEvaluation.programId;
        courseInput.evaluation_trimester = trimesterEvaluation.trimester;
        // The id is kept so the backend upserts the question instead of
        // recreating it, which would orphan the teachers' captures.
        courseInput.courseEvaluations = trimesterEvaluation.questions.map(
          (question) => ({
            ...(question.id != null && { id: question.id }),
            related_course_id: question.related_course_id!,
            key_learnings: evaluationQuestionTitle(question),
            rubric_achieved: "",
            rubric_in_process: "",
            rubric_needs_support: "",
            axis: "",
            max_points: question.max_points!,
            tooltip: "",
          }),
        );
      } else if (initialValues.diagnostic) {
        if (evaluationMode === "preguntas") {
          // Diagnóstico por preguntas (Primaria): cada pregunta es un
          // CourseEvaluation con puntaje + tooltip y sin rúbricas. Se
          // conserva el id para que el backend haga upsert y no huerfane
          // las capturas de profesores.
          const validQuestions = courseEvaluations.filter(
            (question) => (question.max_points ?? 0) > 0,
          );
          courseInput.courseEvaluations = validQuestions.map(
            (question, index) => ({
              ...(question.id != null && { id: question.id }),
              key_learnings: `Pregunta ${index + 1}`,
              rubric_achieved: "",
              rubric_in_process: "",
              rubric_needs_support: "",
              // Keep the per-question axis (SCRUM-740); it was previously
              // hardcoded to "" and silently dropped on save.
              axis: question.axis ?? "",
              max_points: question.max_points ?? 1,
              tooltip: question.tooltip || "",
            }),
          );
        } else {
          // Para cursos diagnósticos por rúbricas, enviar las evaluaciones múltiples (el eje es opcional)
          const validEvaluations = courseEvaluations.filter(
            (evaluation) =>
              evaluation.key_learnings ||
              evaluation.rubric_achieved ||
              evaluation.rubric_in_process ||
              evaluation.rubric_needs_support,
          );
          courseInput.courseEvaluations = validEvaluations;
        }
      } else {
        // Para cursos regulares, enviar los campos individuales como antes
        courseInput.key_learnings = initialValues.key_learnings;
        courseInput.rubric_achieved = initialValues.rubric_achieved;
        courseInput.rubric_in_process = initialValues.rubric_in_process;
        courseInput.rubric_needs_support = initialValues.rubric_needs_support;
        courseInput.axis = initialValues.axis;
      }

      await handleSubmitFormResponseManage(
        courseInput,
        createCourse,
        updateCourseWithCascade,
        navigate,
        dispatch,
        "course",
        "addCourse",
        "updateCourse",
        courseId,
        (error: any) => {
          showDuplicateClave(error);
          notification.error({
            message: "Error",
            description: getApiErrorMessage(
              error,
              "Ocurrió un error inesperado.",
            ),
            placement: "top",
            duration: 5,
          });
        },
      );
    } catch (error: any) {
      showDuplicateClave(error);
      // Also covers project sub-operation failures during submit
      // (createProjectService/updatePhaseTitle/syncProjectCourses/etc.),
      // which previously only logged and left the user with no feedback.
      const backendMessage = error?.response?.data?.message;
      notify(
        "error",
        "Error",
        backendMessage
          ? `Ocurrió un error al guardar. ${backendMessage}`
          : "Ocurrió un error al guardar. Por favor, intenta nuevamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangeTags = (values: string[]) => {
    setTagsSelected(values);
  };

  const handleRemoveLesson = (index: number) => {
    setSelectedLessons(selectedLessons.filter((_, i) => i !== index));
  };

  const handleAddLessonAtIndex = (index: number) => {
    const newLesson = { id: 0, title: "", _uid: nextLessonRowUid() };
    const updatedLessons = [
      ...selectedLessons.slice(0, index),
      newLesson,
      ...selectedLessons.slice(index),
    ];
    setSelectedLessons(updatedLessons);
  };

  const handleLessonChange = (
    index: number,
    field: string,
    value: string | number | undefined,
  ) => {
    const updatedLessons = [...selectedLessons];
    updatedLessons[index] = { ...updatedLessons[index], [field]: value };
    setSelectedLessons(updatedLessons);
  };

  const handleReorderLessons = (reordered: LessonSelected[]) => {
    // order_by is persisted from the array index on submit, so reordering the
    // array is all that is needed — no extra call, no autosave.
    setSelectedLessons(reordered);
  };

  // Funciones para manejar cursos relacionados (proyectos)
  const handleRemoveCourse = (index: number) => {
    setSelectedCourses(selectedCourses.filter((_, i) => i !== index));
  };

  const handleAddCourseAtIndex = (index: number) => {
    const newCourse = {
      id: 0,
      title: "",
      program_id: null,
      week: null,
    };
    const updatedCourses = [
      ...selectedCourses.slice(0, index + 1),
      newCourse,
      ...selectedCourses.slice(index + 1),
    ];
    setSelectedCourses(updatedCourses);
  };

  const handleCourseChange = (index: number, courseId: number) => {
    // Buscar en allCourses si estamos en un proyecto, sino en courses
    const coursesToSearch: { id: number; title: string }[] =
      allCourses.length > 0 ? allCourses : courses;
    const course = coursesToSearch.find((c) => c.id === courseId);
    if (course) {
      const updatedCourses = [...selectedCourses];
      updatedCourses[index] = {
        id: course.id,
        title: course.title,
        program_id: null,
        week: null,
      };
      setSelectedCourses(updatedCourses);

      // Cargar programas asociados a este curso si no están ya cargados
      if (!programsbyCourse[courseId]) {
        getProgramsByCourse(courseId)
          .then((progs) => {
            setProgramsbyCourse((prev) => ({ ...prev, [courseId]: progs }));
          })
          .catch((error) => {
            console.error("Error loading programs for course:", error);
            notify(
              "error",
              "Error",
              "No se pudieron cargar los programas del curso."
            );
          });
      }
    }
  };

  // Funciones para manejar fases del proyecto
  const handlePhaseTitleChange = (phaseIdOrIndex: number, title: string) => {
    const updatedPhases = projectPhases.map((phase) => {
      // Comparar directamente por ID (funciona tanto con IDs negativos temporales como positivos reales)
      if (phase.id === phaseIdOrIndex) {
        return { ...phase, title };
      }
      return phase;
    });

    setProjectPhases(updatedPhases);
  };

  // Funciones para manejar evaluaciones múltiples
  const handleAddEvaluation = () => {
    if (courseEvaluations.length >= 10) {
      notify(
        "warning",
        "Límite alcanzado",
        "No se pueden agregar más de 10 evaluaciones",
      );
      return;
    }
    setCourseEvaluations([
      ...courseEvaluations,
      {
        key_learnings: "",
        rubric_achieved: "",
        rubric_in_process: "",
        rubric_needs_support: "",
        axis: "",
      },
    ]);
  };

  // Cambio de modo del curso diagnóstico (rúbricas ↔ preguntas). Si hay
  // contenido cargado se confirma antes: al guardar, las evaluaciones del
  // modo anterior se eliminan y las capturas de profesores quedan huérfanas.
  const handleEvaluationModeChange = (mode: DiagnosticEvaluationMode) => {
    if (mode === evaluationMode) return;

    const resetForMode = () => {
      setEvaluationMode(mode);
      setCourseEvaluations(
        mode === "preguntas"
          ? [createEmptyQuestion()]
          : [
              {
                key_learnings: "",
                rubric_achieved: "",
                rubric_in_process: "",
                rubric_needs_support: "",
                axis: "",
              },
            ],
      );
    };

    const hasContent = courseEvaluations.some(
      (evaluation) =>
        evaluation.key_learnings ||
        evaluation.rubric_achieved ||
        evaluation.rubric_in_process ||
        evaluation.rubric_needs_support ||
        evaluation.tooltip ||
        evaluation.max_points != null,
    );

    if (!hasContent) {
      resetForMode();
      return;
    }

    Modal.confirm({
      title: "¿Cambiar el modo de evaluación?",
      content:
        "Al guardar se reemplazarán las evaluaciones actuales del curso y las capturas ya registradas por profesores dejarán de mostrarse.",
      okText: "Cambiar",
      cancelText: "Cancelar",
      onOk: resetForMode,
    });
  };

  // Funciones para manejar rúbricas de proyectos
  const handleAddProjectRubric = () => {
    if (projectRubrics.length >= 10) {
      notify(
        "warning",
        "Límite alcanzado",
        "No se pueden agregar más de 10 rúbricas",
      );
      return;
    }
    setProjectRubrics([
      ...projectRubrics,
      {
        rubric_achieved: "",
        rubric_in_process: "",
        rubric_needs_support: "",
        key_learnings: "",
      },
    ]);
  };

  const handleRemoveProjectRubric = (index: number) => {
    setProjectRubrics(projectRubrics.filter((_, i) => i !== index));
  };

  const handleProjectRubricChange = (
    index: number,
    field: string,
    value: string,
  ) => {
    const updatedRubrics = [...projectRubrics];
    updatedRubrics[index] = { ...updatedRubrics[index], [field]: value };
    setProjectRubrics(updatedRubrics);
  };

  const handleRemoveEvaluation = (index: number) => {
    setCourseEvaluations(courseEvaluations.filter((_, i) => i !== index));
  };

  const checkEvaluationContent = (index: number): boolean => {
    const evaluation = courseEvaluations[index];
    return !!(
      evaluation.key_learnings ||
      evaluation.rubric_achieved ||
      evaluation.rubric_in_process ||
      evaluation.rubric_needs_support
    );
  };

  const handleEvaluationChange = (
    index: number,
    field: keyof CourseEvaluation,
    value: string,
  ) => {
    const updatedEvaluations = [...courseEvaluations];
    updatedEvaluations[index] = {
      ...updatedEvaluations[index],
      [field]: value,
    };
    setCourseEvaluations(updatedEvaluations);
  };

  const handleDiagnosticChange = (checked: boolean) => {
    setInitialValues({
      ...initialValues,
      diagnostic: checked,
    });

    // Si cambia a diagnóstico y no hay evaluaciones, agregar una
    if (checked && courseEvaluations.length === 0) {
      setCourseEvaluations([
        {
          key_learnings: "",
          rubric_achieved: "",
          rubric_in_process: "",
          rubric_needs_support: "",
          axis: "",
        },
      ]);
    }
  };

  // The cascade modal must stay mounted even while a submit is in flight
  // (loading=true): the previous early `return <ActivityIndicator />`
  // unmounted it mid-submit, silently swallowing the cascade confirm dialog.
  const isPageLoading =
    loading ||
    loadingLessons ||
    loadingSubjects ||
    loadingCourses ||
    loadingPrograms;

  return (
    <>
      {cascadeContext && (
        <CascadeConfirmModal
          open
          preview={cascadeContext.preview}
          confirmLoading={cascadeConfirming}
          onConfirm={handleCascadeConfirm}
          onCancel={handleCascadeCancel}
        />
      )}
      {isPageLoading ? (
        <ActivityIndicator />
      ) : (
      <div className="container">
      {courseId ? (
        <>
          <Title level={3}>Editar curso</Title>
          <LastModified
            entityType={isEditingProject ? "projects" : "courses"}
            entityId={courseId}
          />
        </>
      ) : (
        <Title level={3}>Crear curso</Title>
      )}
      {!courseId && (
        <Tabs
          activeKey={courseType}
          onChange={(key) => {
            setCourseType(key as CourseType);
            if (key === "diagnostic" || key === "project") {
              setInitialValues({
                ...initialValues,
                diagnostic: key === "diagnostic",
                subject_id: key === "project" ? 7 : initialValues.subject_id,
              });
              if (key === "diagnostic" && courseEvaluations.length === 0) {
                setCourseEvaluations([
                  {
                    key_learnings: "",
                    rubric_achieved: "",
                    rubric_in_process: "",
                    rubric_needs_support: "",
                    axis: "",
                  },
                ]);
              }
              if (key === "project" && projectRubrics.length === 0) {
                setProjectRubrics([
                  {
                    key_learnings: "",
                    rubric_achieved: "",
                    rubric_in_process: "",
                    rubric_needs_support: "",
                  },
                ]);
              }
            } else {
              // Leaving Proyectos (subject 7) leaves Asignatura showing the
              // earlier choice. Evaluaciones adopts it, so its program list
              // matches what the admin sees; Normal keeps clearing it.
              const subjectAfterProject =
                key === "evaluation"
                  ? form.getFieldValue("subject_id") ?? 0
                  : 0;
              setInitialValues({
                ...initialValues,
                diagnostic: false,
                subject_id:
                  initialValues.subject_id === 7
                    ? subjectAfterProject
                    : initialValues.subject_id,
              });
            }
          }}
          items={[
            {
              key: "normal",
              label: "Normal",
            },
            {
              key: "diagnostic",
              label: "Diagnóstico",
            },
            {
              key: "evaluation",
              label: "Evaluaciones",
            },
            {
              key: "project",
              label: "Proyectos",
            },
          ]}
          style={{ marginBottom: 24 }}
        />
      )}
      {courseId && initialCourseType !== "normal" && (
        <div
          style={{
            padding: "12px 16px",
            background: "var(--bo-warn-bg)",
            border: "1px solid var(--bo-warn-border)",
            borderRadius: "4px",
            marginBottom: "24px",
            fontSize: "14px",
          }}
        >
          <strong>Tipo de curso:</strong>{" "}
          {initialCourseType === "diagnostic"
            ? "Diagnóstico"
            : initialCourseType === "evaluation"
              ? "Evaluación"
              : "Proyecto"}
          <div style={{ color: "#ff7875", fontSize: "12px", marginTop: "4px" }}>
            No puedes cambiar el tipo de curso una vez creado.
          </div>
        </div>
      )}
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        className="form"
        onFinish={handleSubmit}
      >
        <Form.Item label="Título" name="title">
          <Input
            value={initialValues.title}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setInitialValues({
                ...initialValues,
                title: e.target.value,
              })
            }
          />
        </Form.Item>

        <Form.Item
          label="Clave"
          name="clave"
          validateStatus={claveError ? "error" : undefined}
          help={claveError ?? undefined}
        >
          <Input
            value={initialValues.clave ?? ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setClaveError(null);
              setInitialValues({
                ...initialValues,
                clave: e.target.value,
              });
            }}
          />
        </Form.Item>

        {/* Ocultar selector de asignatura para proyectos */}
        {courseType !== "project" &&
          (!courseId || initialCourseType !== "project") && (
            <Form.Item label="Asignatura" name="subject_id">
              <Select
                value={initialValues.subject_id}
                onChange={(value) => {
                  setInitialValues({
                    ...initialValues,
                    subject_id: value,
                  });
                  // The chosen program belongs to the previous subject.
                  setTrimesterEvaluation(emptyTrimesterEvaluation);
                }}
                disabled={
                  courseId !== undefined &&
                  (initialCourseType === "diagnostic" ||
                    initialCourseType === "evaluation")
                }
              >
                {subjects
                  .filter((subject) => subject.id !== 7) // Ocultar Proyectos (id = 7)
                  .map((subject) => (
                    <Option key={subject.id} value={subject.id}>
                      {subject.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}

        {/* NORMAL COURSE FORM */}
        {courseType === "normal" &&
          (!courseId || initialCourseType === "normal") && (
            <NormalCourseForm
              initialValues={initialValues}
              setInitialValues={setInitialValues}
              getEvaluationFieldsValidation={getEvaluationFieldsValidation}
              tagsSelected={tagsSelected}
              handleChangeTags={handleChangeTags}
              selectedLessons={selectedLessons}
              availableLessons={availableLessons}
              handleRemoveLesson={handleRemoveLesson}
              handleAddLessonAtIndex={handleAddLessonAtIndex}
              handleLessonChange={handleLessonChange}
              handleReorderLessons={handleReorderLessons}
              allCourses={allCourses}
              relevantCourses={relevantCourses}
              setRelevantCourses={setRelevantCourses}
              programs={programs}
              currentCourseId={courseId}
              nemPrimaryLinkings={nemPrimaryLinkings}
              setNemPrimaryLinkings={setNemPrimaryLinkings}
            />
          )}

        {/* DIAGNOSTIC COURSE FORM */}
        {(courseType === "diagnostic" ||
          (courseId && initialCourseType === "diagnostic")) && (
          <DiagnosticCourseForm
            courseEvaluations={courseEvaluations}
            setCourseEvaluations={setCourseEvaluations}
            handleAddEvaluation={handleAddEvaluation}
            handleRemoveEvaluation={handleRemoveEvaluation}
            handleEvaluationChange={handleEvaluationChange}
            checkEvaluationContent={checkEvaluationContent}
            evaluationMode={evaluationMode}
            onEvaluationModeChange={handleEvaluationModeChange}
            availableAxes={availableQuestionAxes}
            selectedLessons={selectedLessons}
            availableLessons={availableLessons}
            handleRemoveLesson={handleRemoveLesson}
            handleAddLessonAtIndex={handleAddLessonAtIndex}
            handleLessonChange={handleLessonChange}
            handleReorderLessons={handleReorderLessons}
            allCourses={allCourses}
            relevantCourses={relevantCourses}
            setRelevantCourses={setRelevantCourses}
            programs={programs}
            currentCourseId={courseId}
          />
        )}

        {/* EVALUATION COURSE FORM */}
        {(courseType === "evaluation" ||
          (courseId && initialCourseType === "evaluation")) && (
          <EvaluationCourseForm
            // A new subject starts the program choice over.
            key={initialValues.subject_id}
            value={trimesterEvaluation}
            onChange={setTrimesterEvaluation}
            subjectId={initialValues.subject_id}
            programs={programs}
            selectedLessons={selectedLessons}
            availableLessons={availableLessons}
            handleRemoveLesson={handleRemoveLesson}
            handleAddLessonAtIndex={handleAddLessonAtIndex}
            handleLessonChange={handleLessonChange}
            handleReorderLessons={handleReorderLessons}
          />
        )}

        {/* PROJECT COURSE FORM */}
        {(courseType === "project" ||
          (courseId && initialCourseType === "project")) && (
          <ProjectCourseForm
            initialValues={initialValues}
            setInitialValues={setInitialValues}
            selectedCourses={selectedCourses}
            setSelectedCourses={setSelectedCourses}
            selectedLessons={selectedLessons}
            setSelectedLessons={setSelectedLessons}
            projectRubrics={projectRubrics}
            setProjectRubrics={setProjectRubrics}
            courses={allCourses.length > 0 ? allCourses : courses}
            availableLessons={availableLessons}
            projectPhases={projectPhases}
            setProjectPhases={setProjectPhases}
            uploadingProjectImage={uploadingProjectImage}
            projectImageUrl={projectImageUrl}
            handleProjectImageUpload={handleProjectImageUpload}
            handleRemoveCourse={handleRemoveCourse}
            handleAddCourseAtIndex={handleAddCourseAtIndex}
            handleCourseChange={handleCourseChange}
            handleRemoveLesson={handleRemoveLesson}
            handleAddLessonAtIndex={handleAddLessonAtIndex}
            handleLessonChange={handleLessonChange}
            handleAddProjectRubric={handleAddProjectRubric}
            handleRemoveProjectRubric={handleRemoveProjectRubric}
            handleProjectRubricChange={handleProjectRubricChange}
            handlePhaseTitleChange={handlePhaseTitleChange}
            programs={programs}
            programsbyCourse={programsbyCourse}
            handleCourseReorder={(newCourses) => setSelectedCourses(newCourses)}
          />
        )}

        {/* SELECTOR DE RECURSOS DEL CURSO (solo para cursos normales y diagnósticos) */}
        {courseType !== "project" &&
          (!courseId || initialCourseType !== "project") && (
            <CourseResourceSelector
              items={courseResourceRows}
              resources={resources}
              onChange={setCourseResourceRows}
            />
          )}

        <Form.Item>
          <Button type="primary" htmlType="submit" disabled={loadFailed}>
            {courseType === "project" ||
            (courseId && initialCourseType === "project")
              ? "Guardar Proyecto"
              : courseType === "diagnostic" ||
                  (courseId && initialCourseType === "diagnostic")
                ? "Guardar curso diagnóstico"
                : courseType === "evaluation" ||
                    (courseId && initialCourseType === "evaluation")
                  ? "Guardar curso de evaluación"
                  : "Guardar curso"}
          </Button>
        </Form.Item>
      </Form>

      {courseId && (
        <Collapse
          items={[
            {
              key: "history",
              label: "Historial de cambios",
              children: (
                <EntityHistory
                  entityType={isEditingProject ? "projects" : "courses"}
                  entityId={courseId}
                />
              ),
            },
          ]}
        />
      )}
      </div>
      )}
    </>
  );
};

export default CourseForm;
